import React, { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useAsync from 'react-use/esm/useAsync';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { ErrorPanel, InfoCard, Progress } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  AdrContentDecorator,
  AdrFileInfo,
  AdrReader,
  adrApiRef,
} from '@backstage-community/plugin-adr';
import {
  getAdrLocationUrl,
  madrFilePathFilter,
} from '@backstage-community/plugin-adr-common';
import { TinyChip, TinyChipProps } from '../../TinyChip';

const useStyles = makeStyles(theme => ({
  item: {
    gap: theme.spacing(1),
  },
  number: {
    flexShrink: 0,
    fontFamily: 'monospace',
    color: theme.palette.text.secondary,
  },
  title: {
    flexGrow: 1,
    minWidth: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1, 0, 2),
  },
  // What is left of the list when it is hidden, above the ADR: the button to
  // show it, then the pager below - on one line, the titles cut short where
  // the line is too narrow for them.
  strip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5, 0.5, 0.5),
    marginBottom: theme.spacing(1),
    minWidth: 0,
  },
  // Previous, open and next ADR. The outer columns share the room left over
  // equally, so the open ADR stays in the middle with a neighbour missing.
  pager: {
    flexGrow: 1,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    gap: theme.spacing(3),
    minWidth: 0,
  },
  previous: {
    display: 'flex',
    minWidth: 0,
  },
  next: {
    display: 'flex',
    justifyContent: 'flex-end',
    minWidth: 0,
  },
  current: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  currentTitle: {
    fontWeight: 500,
    minWidth: 0,
  },
  position: {
    flexShrink: 0,
  },
  neighbour: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
}));

/** `ADR-0028 - Accounting and billing`, the way the Magic ADRs are titled. */
const NUMBERED_TITLE = /^ADR-(\d+)\s*[-–:]\s*(.+)$/i;

/**
 * The number and the rest of the title of an ADR, so that the list can show
 * the number small in front of the title instead of as part of it. A title
 * without a number is all title.
 *
 * Exported for the sake of its test.
 */
export function splitTitle(title: string): { number?: string; title: string } {
  const match = title.match(NUMBERED_TITLE);
  return match ? { number: match[1], title: match[2] } : { title };
}

/**
 * Puts the `Status:` and `Date:` list MADR has under the title into one line
 * right below the title - `*Proposed · 2026-10-26*` - which takes less room
 * than the list and still tells both with the list of ADRs hidden. Only the
 * lines before the first section count, not a list item further down that
 * happens to start the same. The line ends in a link to the file in its
 * repository - the reader opens links in a new tab. Takes out the `[TOC]`
 * marker too, which the reader shows as text.
 *
 * Exported for the sake of its test.
 */
export const compactMadrHeader: AdrContentDecorator = ({
  content,
  baseUrl,
  filename,
}) => {
  const firstSection = content.search(/^## /m);
  const head = firstSection === -1 ? content : content.slice(0, firstSection);
  const body = firstSection === -1 ? '' : content.slice(firstSection);

  const values: string[] = [];
  let cleanHead = head.replace(
    /^[-*]\s+(?:\*\*)?(?:Status|Date)(?:\*\*)?\s*:\s*(.*)\n?/gim,
    (_line, value: string) => {
      if (value.trim()) {
        values.push(value.trim());
      }
      return '';
    },
  );
  // `baseUrl` is the directory of the ADRs, `filename` the path of this one
  // in it. The angle brackets keep a URL with spaces or parentheses whole.
  const source =
    baseUrl && filename
      ? `[${/gitlab/i.test(baseUrl) ? 'Open in GitLab' : 'View source'}](<${baseUrl}/${filename}>)`
      : undefined;
  const parts = [
    ...(values.length ? [`*${values.join(' · ')}*`] : []),
    ...(source ? [source] : []),
  ];
  if (parts.length) {
    const line = `${parts.join(' · ')}\n\n`;
    const title = cleanHead.match(/^# .*\n+/m);
    cleanHead =
      title?.index === undefined
        ? `${line}${cleanHead}`
        : `${cleanHead.slice(0, title.index + title[0].length)}${line}${cleanHead.slice(title.index + title[0].length)}`;
  }

  return {
    content: `${cleanHead}${body}`.replace(/^\[TOC\]\s*$\n?/gim, ''),
  };
};

/** The decorators of the plugin, and ours after them. */
const decorators: AdrContentDecorator[] = [
  AdrReader.decorators.createRewriteRelativeLinksDecorator(),
  AdrReader.decorators.createRewriteRelativeEmbedsDecorator(),
  AdrReader.decorators.createFrontMatterFormatterDecorator(),
  compactMadrHeader,
];

/** The colours the plugin gives a status, the rest stays neutral. */
function statusColor(status: string): TinyChipProps['color'] {
  switch (status.toLocaleLowerCase('en-US')) {
    case 'accepted':
      return 'success';
    case 'deprecated':
    case 'superseded':
      return 'warning';
    case 'rejected':
      return 'error';
    default:
      return 'neutral';
  }
}

/**
 * Scrolls every scrolled box the element is in back to the top, and the
 * window: a Backstage page scrolls in its own box rather than the window, and
 * which one that is depends on the layout.
 */
function scrollToTop(element: HTMLElement | null) {
  for (let node = element?.parentElement; node; node = node.parentElement) {
    if (node.scrollTop > 0) {
      node.scrollTo({ top: 0 });
    }
  }
  window.scrollTo({ top: 0 });
}

const AdrListItem = (props: {
  adr: AdrFileInfo;
  selected: boolean;
  onClick: () => void;
}) => {
  const { adr, selected, onClick } = props;
  const classes = useStyles();
  const { number, title } = splitTitle(
    adr.title ?? adr.name.replace(/\.md$/, ''),
  );
  return (
    <ListItem
      button
      component={Link}
      to={`?record=${encodeURIComponent(adr.path)}`}
      selected={selected}
      onClick={onClick}
      className={classes.item}
    >
      {number && (
        <Typography variant="caption" className={classes.number}>
          {number}
        </Typography>
      )}
      <Tooltip title={adr.date ? `${title} - ${adr.date}` : title}>
        <Typography variant="body2" noWrap className={classes.title}>
          {title}
        </Typography>
      </Tooltip>
      {adr.status && (
        <TinyChip
          label={adr.status}
          tooltip="Status of the decision"
          color={statusColor(adr.status)}
        />
      )}
    </ListItem>
  );
};

/**
 * The ADR next to the open one in the list - arrow, number and title - as a
 * link. Nothing at an end of the list.
 */
const NeighbourLink = (props: {
  adr?: AdrFileInfo;
  icon: React.ReactElement;
  onClick: () => void;
}) => {
  const { adr, icon, onClick } = props;
  const classes = useStyles();
  if (!adr) {
    return null;
  }
  return (
    <Link
      to={`?record=${encodeURIComponent(adr.path)}`}
      onClick={onClick}
      className={classes.neighbour}
    >
      {icon}
      <AdrLabel adr={adr} />
    </Link>
  );
};

/** The number of an ADR, small, and its title, on one line cut short. */
const AdrLabel = (props: { adr: AdrFileInfo; className?: string }) => {
  const { adr, className } = props;
  const classes = useStyles();
  const { number, title } = splitTitle(
    adr.title ?? adr.name.replace(/\.md$/, ''),
  );
  return (
    <Typography variant="body2" noWrap className={className}>
      {number && <span className={classes.number}>{number} </span>}
      {title}
    </Typography>
  );
};

const STORAGE_KEY = 'rtl/adrs/list-collapsed';

/*
 * Whether the list was hidden, kept in the browser's storage so that it stays
 * hidden on the next ADR and the next visit. A browser that blocks site
 * storage throws on the mere access: the list is shown then, and the choice
 * lasts as long as the tab is open - as in `PinnedSubmenu` of the app.
 */
function storedCollapsed(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function storeCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(collapsed));
  } catch {
    // Not remembered then, see storedCollapsed.
  }
}

/**
 * The ADRs tab, in place of the plugin's `EntityAdrContent`: one line per ADR
 * - its number, its title, its status, the date on hover - instead of two, and
 * the ADR opened from the list starts at the top of the page rather than
 * where the list was scrolled to. The list can be hidden, which gives the ADR
 * the whole width and arrows to the ADRs above and below it in the list. The
 * ADR itself is shown by the plugin's reader.
 *
 * It needs no route of the plugin, unlike `EntityAdrContent`, whose route the
 * app only finds when the tab sits in the element tree of `EntityPage.tsx`.
 */
export const AdrContent = () => {
  const { entity } = useEntity();
  const scmIntegrations = useApi(scmIntegrationsApiRef);
  const adrApi = useApi(adrApiRef);
  const [searchParams] = useSearchParams();
  const rootRef = useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const [collapsed, setCollapsed] = useState(storedCollapsed);

  const {
    value: adrs,
    loading,
    error,
  } = useAsync(async () => {
    const { data } = await adrApi.listAdrs(
      getAdrLocationUrl(entity, scmIntegrations),
    );
    return data.filter(
      adr => adr.type === 'file' && madrFilePathFilter(adr.path),
    );
  }, [entity, scmIntegrations, adrApi]);

  if (loading) {
    return <Progress />;
  }
  if (error) {
    return <ErrorPanel title="Could not read the ADRs" error={error} />;
  }
  if (!adrs?.length) {
    return <Typography>No ADRs found.</Typography>;
  }

  // The first ADR until one is picked.
  const record = searchParams.get('record');
  const index = Math.max(
    adrs.findIndex(adr => adr.path === record),
    0,
  );
  const selected = adrs[index].path;
  const onOpen = () => scrollToTop(rootRef.current);

  const toggle = () => {
    storeCollapsed(!collapsed);
    setCollapsed(!collapsed);
  };

  return (
    <div ref={rootRef}>
      <Grid container spacing={3}>
        {!collapsed && (
          <Grid item xs={12} md={4}>
            <InfoCard noPadding>
              <Box className={classes.header}>
                <Typography variant="subtitle2">
                  Decisions ({adrs.length})
                </Typography>
                <Tooltip title="Hide the list of ADRs">
                  <IconButton
                    size="small"
                    aria-label="Hide the list of ADRs"
                    onClick={toggle}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <List dense>
                {adrs.map(adr => (
                  <AdrListItem
                    key={adr.path}
                    adr={adr}
                    selected={adr.path === selected}
                    onClick={onOpen}
                  />
                ))}
              </List>
            </InfoCard>
          </Grid>
        )}
        <Grid item xs>
          {collapsed && (
            <Paper className={classes.strip}>
              <Tooltip title="Show the list of ADRs">
                <IconButton
                  size="small"
                  aria-label="Show the list of ADRs"
                  onClick={toggle}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>
              {/* With the list hidden, the ADRs next to this one in the list
                  are reached from here, on either side of the open one. */}
              <Box className={classes.pager}>
                <Box className={classes.previous}>
                  <NeighbourLink
                    adr={adrs[index - 1]}
                    icon={<KeyboardArrowUpIcon fontSize="small" />}
                    onClick={onOpen}
                  />
                </Box>
                <Box className={classes.current}>
                  <AdrLabel
                    adr={adrs[index]}
                    className={classes.currentTitle}
                  />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.position}
                  >
                    · {index + 1} of {adrs.length}
                  </Typography>
                </Box>
                <Box className={classes.next}>
                  <NeighbourLink
                    adr={adrs[index + 1]}
                    icon={<KeyboardArrowDownIcon fontSize="small" />}
                    onClick={onOpen}
                  />
                </Box>
              </Box>
            </Paper>
          )}
          <AdrReader adr={selected} decorators={decorators} />
        </Grid>
      </Grid>
    </div>
  );
};
